/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/img-redundant-alt */
import { Box, Chip, makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import React, { FC, ReactElement, useContext, useEffect, useRef, useState } from 'react';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Magnifier } from 'react-image-magnifiers';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { Builder } from '@builder.io/react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useShopConfig } from '../../hooks/shopConfig';
import ActionBarMaybe from './ActionBarMaybe';
import {
  FEATURED_LISTING_IMAGE_TYPE,
  FEATURED_LISTING_IMAGE_TYPE_TAB_NAMES,
} from '../../shopConfig/config';
import { useDefaultShopStyles } from '../../hooks/useDefaultShopStyles';
import {
  formatUploadcareImageForDisplay,
  getLargeUploadcareImagePreviewUrl,
} from '../../util/uploadcare';
import { useEnabledCustomerExperiences } from '../../hooks/useEnabledCustomerExperiences';
import { getStockImagesForListing } from '../../util/listings/listingImages';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { AvatarSmall } from '../../components/Avatar/Avatar';
import { IconUserFilled, InlineTextButton, TypographyWrapper } from '../../components';
import {
  TypographyFormat,
  TypographyWeight,
} from '../../components/TypographyWrapper/TypographyWrapper';
import AppContext from '../../context/AppContext';
import { trackZoomPDPImage } from '../../util/heap';
import { types as sdkTypes } from '../../util/sdkLoader';
import ActionBarMaybeDesktop from './ActionBarMaybeDesktop';
import {
  createSlug,
  LISTING_PAGE_DRAFT_VARIANT,
  ListingPageParamType,
} from '../../util/urlHelpers';
import { useCurrentListing } from './hooks/useCurrentListing';
import { BackgroundBlurBox } from '../../components/BackgroundBlurBox/BackgroundBlurBox';
import { shouldRenderBackgroundBlurFn } from './SectionImages.utils';
import { setHasFirstMediaSrcLoaded } from './ListingPage.duck';
import css from './ListingPage.module.css';
import './ListingPageSwiper.css';
import { User } from '../../types/sharetribe/user';
import { Listing } from '../../types/sharetribe/listing';
import { ShopColors } from '../../util/colorPalettes';

const { UUID } = sdkTypes;

const FALLBACK_IMAGE_RATIO_FOR_VERTICAL_IMAGES = 1;
const CAROUSEL_ELEMENT_IDENTIFIER = '.carouselImagesWrapper';
const MEDIA_QUERY_LARGE_CAROUSEL_THUMBNAIL_WIDTH = '120px';
// Keep in sync with marketplace.css
const TOPBAR_HEIGHT_DESKTOP = 84;
const MIN_FULL_SCREEN_CAROUSEL_HEIGHT = 700;
const DEFAULT_IMAGE_RESIZE_WIDTH = 500;
const DEFAULT_IMAGE_RESIZE_WIDTH_FOR_DESKTOP = 800;

const MEDIA_TYPE = {
  VIDEO: 'video',
  IMAGE: 'img',
} as const;

const useImageChipStyles = makeStyles({
  root: {
    color: 'white',
    height: '42px',
    fontSize: '16px',
  },
});

interface ImageChipProps {
  backgroundColor?: string;
  avatar?: React.ReactElement;
  label?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

const ImageChip: FC<ImageChipProps> = (props) => {
  const { backgroundColor, avatar, label, className, style } = props;
  const classes = useImageChipStyles();
  return (
    <Chip
      classes={classes}
      avatar={avatar}
      label={label}
      className={className}
      style={{ ...style, backgroundColor }}
    />
  );
};

interface MagnifyImageProps {
  imageSrc: string;
  largeImageSrc?: string;
  imageAlt: string;
  listingId: string;
  onLoad?: () => void;
  onError?: () => void;
}

const MagnifyImage: FC<MagnifyImageProps> = ({
  imageSrc,
  largeImageSrc,
  imageAlt,
  listingId,
  onLoad,
  onError,
}) => {
  const { treetId } = useContext(AppContext);
  const [loadedLargeImageSrc, setLoadedLargeImageSrc] = useState<string | undefined>(undefined);
  const [isHovering, setIsHovering] = useState(false);

  const handleZoomStart = () => trackZoomPDPImage(listingId, imageSrc, treetId);

  // Preload high-res image on hover
  useEffect(() => {
    let canceled = false;

    if (isHovering && largeImageSrc && loadedLargeImageSrc !== largeImageSrc) {
      const img = new Image();
      img.onload = () => {
        if (!canceled) setLoadedLargeImageSrc(largeImageSrc);
      };
      img.onerror = () => {
        if (!canceled) onError?.();
      };
      img.src = largeImageSrc;
    }

    return () => {
      canceled = true;
    };
  }, [isHovering, largeImageSrc, loadedLargeImageSrc, onError]);

  return imageSrc ? (
    <div
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onFocus={() => setIsHovering(true)}
      onBlur={() => setIsHovering(false)}
    >
      <Magnifier
        // Can't use CSS Modules to target Magnifier divs
        className="magnifier"
        imageSrc={imageSrc}
        largeImageSrc={loadedLargeImageSrc || imageSrc} // Use loaded high-res or fallback to regular
        imageAlt={imageAlt}
        dragToMove={false}
        onZoomStart={handleZoomStart}
        onImageLoad={onLoad}
      />
    </div>
  ) : (
    <img src={imageSrc} alt={imageAlt} onLoad={onLoad} onError={onError} />
  );
};

interface StockOrSellerChipProps {
  isStock: boolean;
  isVideo: boolean;
  user: User;
}

const StockOrSellerChip: FC<StockOrSellerChipProps> = ({ isStock, isVideo, user }) => {
  const { css: shopCss } = useShopConfig();
  const { allowSell } = useEnabledCustomerExperiences();
  const defaultShopStyles = useDefaultShopStyles();

  const userIsCurrentUser = user && user.type === 'currentUser';
  const ensuredUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);

  const chipTitle = isStock ? (isVideo ? 'Stock Video' : 'Stock Image') : 'Seller Image';

  let backgroundColor = shopCss?.productBanner?.backgroundColor || defaultShopStyles.shopColor80;
  let fontColor = shopCss?.productBanner?.fontColor;

  if (!allowSell) return null;

  backgroundColor = isStock ? defaultShopStyles.shopColor00 : defaultShopStyles.shopColor100;
  fontColor = isStock ? defaultShopStyles.shopColor80 : fontColor || defaultShopStyles.shopColor10;

  return (
    <ImageChip
      backgroundColor={backgroundColor}
      className={css.imageChipV2}
      style={{ borderRadius: '30px', padding: isStock ? undefined : '0 4px' }}
      avatar={
        isStock ? undefined : (
          <AvatarSmall
            user={ensuredUser}
            disableProfileLink
            className={css.imageChipAvatar}
            noProfileImageIcon={
              <IconUserFilled
                color={defaultShopStyles.shopColor10}
                className={css.imageChipNoProfileImageIcon}
                useTreetDefault
              />
            }
          />
        )
      }
      label={
        <TypographyWrapper variant="body1" typographyOverrides={{ style: { color: fontColor } }}>
          {chipTitle}
        </TypographyWrapper>
      }
    />
  );
};

interface TabProps {
  handleClick: () => void;
  isActive: boolean;
  tabName: string;
  shopColors: ShopColors;
}

const Tab: FC<TabProps> = ({ handleClick, isActive, tabName, shopColors }) => {
  const fontColor = isActive ? shopColors.shopColor100 : shopColors.shopColor60;

  return (
    <InlineTextButton
      onClick={handleClick}
      typographyVariant="body2"
      typographyFormat={isActive ? TypographyFormat.Underlined : undefined}
      typographyWeight={TypographyWeight.Bold}
      style={{ paddingTop: 0, paddingBottom: 0 }}
      typographyOverrides={{
        style: {
          lineHeight: '16px',
          fontSize: '13px',
          color: fontColor,
          paddingLeft: '4px',
          paddingRight: '4px',
          textDecorationColor: fontColor,
        },
      }}
    >
      {tabName}
    </InlineTextButton>
  );
};

interface SwiperCarouselProps {
  carouselMedia: ReactElement[];
  thumbnailImages: ReactElement[];
  numStockMedia: number;
  numSellerMedia: number;
  swiperWrapperRef: React.RefObject<HTMLDivElement>;
  isOwnListing: boolean;
  listing: Listing;
  editParams: {
    id: string;
    slug: string;
    type: ListingPageParamType;
    tab: string;
  };
}

const SwiperCarousel: FC<SwiperCarouselProps> = ({
  carouselMedia,
  thumbnailImages,
  numStockMedia,
  numSellerMedia,
  swiperWrapperRef,
  isOwnListing,
  listing,
  editParams,
}) => {
  const {
    listingFlowConfig: { featuredListingImageType },
  } = useShopConfig();
  const carouselSwiperRef = useRef<any>(null);
  const firstCarouselSlideRef = useRef<any>(null);

  const isMobile = useIsMobile();
  const theme = useTheme();
  const isMediaQueryLarge = useMediaQuery(theme.breakpoints.up('lg'));

  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
  const [swiperLibrary, setSwiperLibrary] = useState<any>(undefined);
  const [swiperReactLibrary, setSwiperReactLibrary] = useState<any>(undefined);
  const [activeTab, setActiveTab] = useState(featuredListingImageType);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const defaultShopStyles = useDefaultShopStyles();

  useEffect(() => {
    const loadData = async () => {
      // Load this in asynchronously
      // because for some reason, it runs into an error with SSR
      const SwiperCore = await import('swiper');
      const SwiperReact = await import('swiper/react');
      const SwiperStyles = await import('./swiperStylesUtils');
      await SwiperStyles.default();
      setSwiperLibrary(SwiperCore);
      setSwiperReactLibrary(SwiperReact);
    };

    loadData();
  }, []);

  const SWIPER_ARROW_STYLE = {
    color: defaultShopStyles.shopColor60,
    backgroundColor: '#fff',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  };

  const firstTab = featuredListingImageType;
  const secondTab = Object.keys(FEATURED_LISTING_IMAGE_TYPE).find((type) => type !== firstTab);
  const secondTabFirstImageIndex =
    featuredListingImageType === FEATURED_LISTING_IMAGE_TYPE.STOCK ? numStockMedia : numSellerMedia;

  const toggleActiveTab = (tab: string) => {
    setActiveTab(tab);
    if (carouselSwiperRef?.current && carouselSwiperRef.current.swiper) {
      const { swiper } = carouselSwiperRef.current;
      const indexToScroll = tab === firstTab ? 0 : secondTabFirstImageIndex;
      swiper.slideTo(indexToScroll);
    }
  };

  const handleSlideChange = debounce((swiper: any) => {
    const imageIndex = swiper.realIndex;
    const isIndexOverSecondTabFirstImageIndex = imageIndex >= secondTabFirstImageIndex;
    setActiveSlideIndex(imageIndex);
    setActiveTab(isIndexOverSecondTabFirstImageIndex ? secondTab! : firstTab);
  }, 200);

  if (!swiperLibrary || !swiperReactLibrary) return null;

  const hasSellerAndStockImages = numStockMedia > 0 && numSellerMedia > 0;

  const { Swiper, SwiperSlide } = swiperReactLibrary;
  const { Navigation, Mousewheel, Thumbs } = swiperLibrary;

  return (
    <div
      ref={swiperWrapperRef}
      style={{ display: 'flex', flexDirection: isMediaQueryLarge ? 'row' : 'column-reverse' }}
    >
      <Box
        maxHeight="100%"
        width={{ xs: '85%', lg: MEDIA_QUERY_LARGE_CAROUSEL_THUMBNAIL_WIDTH }}
        margin={{ xs: '24px auto 0', lg: '0 12px 0 0' }}
      >
        {hasSellerAndStockImages && (
          <Box display="flex" justifyContent="center" mb={2}>
            <Tab
              handleClick={() => toggleActiveTab(firstTab)}
              isActive={activeTab === firstTab}
              tabName={FEATURED_LISTING_IMAGE_TYPE_TAB_NAMES[firstTab]}
              shopColors={defaultShopStyles}
            />
            <Tab
              handleClick={() => toggleActiveTab(secondTab!)}
              isActive={activeTab === secondTab}
              tabName={FEATURED_LISTING_IMAGE_TYPE_TAB_NAMES[secondTab!]}
              shopColors={defaultShopStyles}
            />
          </Box>
        )}
        <Swiper
          onSwiper={setThumbsSwiper}
          modules={[Mousewheel]}
          mousewheel
          navigation={{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }}
          className={classNames(`${css.thumbnailsSwiper} thumbs-wrapper`, {
            [css.thumbnailsSwiperWithTabs]: hasSellerAndStockImages,
          })}
          // cssMode enabled prevents lag on mobile when swiping
          cssMode
          breakpoints={{
            0: {
              direction: 'horizontal',
              slidesPerView: 5,
              spaceBetween: 10,
            },
            1024: {
              direction: 'vertical',
              slidesPerView: 'auto',
              spaceBetween: 10,
            },
          }}
        >
          {thumbnailImages.map((image) => (
            <SwiperSlide key={image.key}>{image}</SwiperSlide>
          ))}
        </Swiper>
      </Box>

      <Box
        maxHeight="100%"
        width={{ xs: '100%', lg: `calc(100% - ${MEDIA_QUERY_LARGE_CAROUSEL_THUMBNAIL_WIDTH})` }}
      >
        <Swiper
          ref={carouselSwiperRef}
          thumbs={{ swiper: thumbsSwiper }}
          onSlideChange={handleSlideChange}
          slidesPerView={1}
          // cssMode enabled prevents lag on mobile when swiping
          cssMode
          pagination={{ clickable: true }}
          scrollbar={{ draggable: true }}
          allowTouchMove={isMobile}
          navigation={{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }}
          className={`${css.carouselMediaSwiper} carouselImagesWrapper`}
          modules={[Navigation, Thumbs]}
        >
          {carouselMedia.map((media) => {
            const isVideo = media.type === MEDIA_TYPE.VIDEO;

            const shouldRenderBackgroundBlur = shouldRenderBackgroundBlurFn(
              media.key,
              listing.attributes.publicData?.images
            );

            return (
              <SwiperSlide ref={firstCarouselSlideRef} key={media.key}>
                {isMediaQueryLarge && (
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    position="absolute"
                    top={0}
                    width="100%"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box width="100%" position="relative">
                      <ActionBarMaybeDesktop
                        isOwnListing={isOwnListing}
                        listing={listing}
                        editParams={editParams}
                      />
                    </Box>
                  </Box>
                )}
                {isVideo ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    style={{ backgroundColor: 'black' }}
                    height={{ md: '100%' }}
                  >
                    <Box maxHeight={{ xs: '400px', md: '100%' }} maxWidth="100%">
                      {media}
                    </Box>
                  </Box>
                ) : (
                  <BackgroundBlurBox
                    imageSrc={media.key!}
                    keyToUse={`${media.key}-${activeSlideIndex}`}
                    shouldRenderBackgroundBlur={shouldRenderBackgroundBlur}
                  >
                    {media}
                  </BackgroundBlurBox>
                )}
              </SwiperSlide>
            );
          })}
          <div className="swiper-button-next" style={SWIPER_ARROW_STYLE}>
            <ChevronRightIcon />
          </div>
          <div className="swiper-button-prev" style={SWIPER_ARROW_STYLE}>
            <ChevronLeftIcon />
          </div>
        </Swiper>
      </Box>
    </div>
  );
};

const SectionImages: FC = () => {
  const {
    maxStockImages,
    chooseStockImagesOption,
    listingFlowConfig: { featuredListingImageType },
    imageRatio,
  } = useShopConfig();

  const params = useParams<{ id: string; slug?: string; variant?: string }>();
  const swiperWrapperRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const { currentListing, isOwnListing } = useCurrentListing();
  const currentListingId = currentListing.id.uuid;

  const dispatch = useDispatch();
  const setHasLoadedFirstMediaSrc = () => dispatch(setHasFirstMediaSrcLoaded());

  const listingId = new UUID(params.id);
  const isDraftVariant = params.variant === LISTING_PAGE_DRAFT_VARIANT;

  const { images: uploadcareImages, stockVideoUrls = [] } =
    currentListing.attributes.publicData || {};

  const theme = useTheme();
  const isMediaQueryLarge = useMediaQuery(theme.breakpoints.up('lg'));

  const [erroredMediaSrcs, setErroredMediaSrcs] = useState<string[]>([]);

  const dynamicallyScaleCarouselSize = () => {
    if (isMobile) return;
    // Based on the image ratio, we want to fix the height of the carousel so we don't
    // resize all the images based on the largest one
    const sliderWrapper = document.querySelector(CAROUSEL_ELEMENT_IDENTIFIER) as HTMLElement;
    if (!sliderWrapper) return;

    // For brands with a low image ratio, we want to make it larger to accommodate for seller
    // images that are usually taken on a phone and thus have a vertical aspect ratio. We do
    // this across every listing so that the images do not jump around while toggling listings
    const displayRatio = imageRatio <= 1 ? FALLBACK_IMAGE_RATIO_FOR_VERTICAL_IMAGES : imageRatio;
    const carouselWidth = sliderWrapper.clientWidth;
    const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    const vhBasedHeight = vh - TOPBAR_HEIGHT_DESKTOP - 120; // extra padding for bottom
    const windowBasedHeight = Math.max(vhBasedHeight, MIN_FULL_SCREEN_CAROUSEL_HEIGHT);
    const heightToUse = `${Math.min(
      Math.round(carouselWidth * displayRatio),
      windowBasedHeight
    )}px`;

    if (swiperWrapperRef.current && isMediaQueryLarge) {
      swiperWrapperRef.current.style.height = heightToUse;
    }
    sliderWrapper.style.height = heightToUse;
  };

  useEffect(() => {
    dynamicallyScaleCarouselSize();
  }, [currentListing]);

  const stockImages =
    maxStockImages > 0 ? getStockImagesForListing(currentListing, chooseStockImagesOption) : [];
  const validStockImages = stockImages
    .filter((src) => !erroredMediaSrcs.includes(src))
    .slice(0, maxStockImages);
  const validStockVideos = stockVideoUrls.filter((src: string) => !erroredMediaSrcs.includes(src));
  const validSellerImages = uploadcareImages || [];

  const numStockMedia = validStockImages.length + validStockVideos.length;
  const numSellerMedia = validSellerImages.length;

  useEffect(() => {
    // If there are no stock or seller images, set the first media src as loaded
    // so that we display the details section.
    if (numStockMedia === 0 && numSellerMedia === 0) {
      setHasLoadedFirstMediaSrc();
    }
  }, [numStockMedia, numSellerMedia]);

  const getStockImageEls = (shouldMagnify: boolean, isFeatured: boolean) =>
    validStockImages.map((src, index) => {
      const imageUrl = formatUploadcareImageForDisplay(
        src,
        undefined,
        undefined,
        isMobile ? DEFAULT_IMAGE_RESIZE_WIDTH : DEFAULT_IMAGE_RESIZE_WIDTH_FOR_DESKTOP
      );
      // For magnify, we can use a larger image for the magnifier
      const largeImageUrl =
        shouldMagnify && !isMobile ? getLargeUploadcareImagePreviewUrl(src) : undefined;

      if (!imageUrl) {
        return null;
      }

      const setHasLoadedFirstMediaSrcFn =
        isFeatured && index === 0 ? setHasLoadedFirstMediaSrc : undefined;

      return shouldMagnify && !isMobile ? (
        <MagnifyImage
          key={imageUrl}
          imageSrc={imageUrl}
          largeImageSrc={largeImageUrl || undefined}
          imageAlt="product stock photo"
          listingId={currentListingId}
          onLoad={setHasLoadedFirstMediaSrcFn}
          onError={setHasLoadedFirstMediaSrcFn}
        />
      ) : (
        <img
          className={css.swiperCarouselImage}
          src={imageUrl}
          key={imageUrl}
          alt="product stock photo"
          onLoad={setHasLoadedFirstMediaSrcFn}
          onError={() => {
            setErroredMediaSrcs([...erroredMediaSrcs, src]);
            if (setHasLoadedFirstMediaSrcFn) setHasLoadedFirstMediaSrcFn();
          }}
        />
      );
    });

  const getStockVideoEls = (shouldAutoplayVideo: boolean, isFeatured: boolean): ReactElement[] =>
    validStockVideos.map((src: string, index: number) => {
      const setHasLoadedFirstMediaSrcFn =
        isFeatured && index === 0 ? setHasLoadedFirstMediaSrc : undefined;

      return (
        <video
          src={src}
          key={src}
          autoPlay={shouldAutoplayVideo && !isMobile}
          controls={isMobile}
          playsInline
          muted
          loop
          className={classNames(css.sectionMediaVideo, css.swiperCarouselImage)}
          width="100%"
          onError={() => {
            setErroredMediaSrcs([...erroredMediaSrcs, src]);
            if (setHasLoadedFirstMediaSrcFn) setHasLoadedFirstMediaSrcFn();
          }}
          onLoadedData={setHasLoadedFirstMediaSrcFn}
        />
      );
    });

  const getSellerImageEls = (shouldMagnify: boolean, isFeatured: boolean) =>
    validSellerImages?.map((image: any, index: number) => {
      // Use appropriately sized images for carousel display
      const imageUrl = formatUploadcareImageForDisplay(
        image.cdnUrl,
        undefined,
        undefined,
        isMobile ? DEFAULT_IMAGE_RESIZE_WIDTH : DEFAULT_IMAGE_RESIZE_WIDTH_FOR_DESKTOP
      );
      // For magnify, we'll lazy load the large version on hover
      const largeImageUrl =
        shouldMagnify && !isMobile ? getLargeUploadcareImagePreviewUrl(image.cdnUrl) : undefined;

      const setHasLoadedFirstMediaSrcFn =
        isFeatured && index === 0 ? setHasLoadedFirstMediaSrc : undefined;

      if (!imageUrl) {
        return null;
      }

      return shouldMagnify && !isMobile ? (
        <MagnifyImage
          key={imageUrl}
          imageSrc={imageUrl}
          largeImageSrc={largeImageUrl || undefined}
          imageAlt="seller uploaded"
          listingId={currentListingId}
          onLoad={setHasLoadedFirstMediaSrcFn}
          onError={setHasLoadedFirstMediaSrcFn}
        />
      ) : (
        <img
          className={css.swiperCarouselImage}
          key={imageUrl}
          src={imageUrl}
          alt="seller uploaded"
          onLoad={setHasLoadedFirstMediaSrcFn}
          onError={setHasLoadedFirstMediaSrcFn}
        />
      );
    }) || [];

  interface CarouselProps {
    shouldDisplayChip?: boolean;
    shouldAutoplayVideo?: boolean;
    shouldMagnify?: boolean;
  }

  const renderCarouselMedia = (carouselProps?: CarouselProps): ReactElement[] => {
    const { shouldDisplayChip, shouldAutoplayVideo, shouldMagnify } = carouselProps || {};

    const areSellerImagesFeatured =
      featuredListingImageType === FEATURED_LISTING_IMAGE_TYPE.USER_UPLOADED;

    const isStockMediaFeatured =
      // Case where seller images are featured but there are no seller images
      !areSellerImagesFeatured || validSellerImages.length === 0;

    const numVideoEls = validStockVideos.length;

    const isStockImageFeatured = isStockMediaFeatured && numVideoEls === 0;
    const stockImageEls = getStockImageEls(shouldMagnify || false, isStockImageFeatured);

    const isStockVideoFeatured = isStockMediaFeatured && numVideoEls > 0;
    const stockVideoEls = getStockVideoEls(shouldAutoplayVideo || false, isStockVideoFeatured);

    const isSellerImageFeatured =
      !isStockMediaFeatured || (stockImageEls.length === 0 && stockVideoEls.length === 0);
    const sellerImageEls = getSellerImageEls(shouldMagnify || false, isSellerImageFeatured);

    let allMediaEls = [];
    if (areSellerImagesFeatured) {
      allMediaEls = [...sellerImageEls, ...stockImageEls, ...stockVideoEls];
    } else {
      allMediaEls = [...stockImageEls, ...stockVideoEls, ...sellerImageEls];
    }

    if (!shouldDisplayChip) return allMediaEls;

    // Add a chip to each image/video to denote if it is a seller or stock image/video
    return allMediaEls.map((mediaEl, index) => {
      const isStock = areSellerImagesFeatured
        ? index >= allMediaEls.length - numStockMedia
        : index < numStockMedia;
      const isVideo = (mediaEl as any).type === MEDIA_TYPE.VIDEO;

      return (
        <Box display="flex" justifyContent="center" height="100%" key={mediaEl.key || index}>
          <StockOrSellerChip isStock={isStock} isVideo={isVideo} user={currentListing.author} />
          {mediaEl}
        </Box>
      );
    });
  };

  const listingSlug = params.slug || createSlug(currentListing.attributes.title || '');
  const listingType = isDraftVariant ? ListingPageParamType.Draft : ListingPageParamType.Edit;
  // Default to whatever page the user is on if it's a draft, otherwise go to the pricing page
  // (most commonly edited page).
  // NOTE: if the default draft tab ever changes, make sure to update abandonedListingEmailJob also.
  const listingTab = isDraftVariant ? 'shipping' : 'pricing';
  const editParams = {
    id: listingId.uuid,
    slug: listingSlug,
    type: listingType,
    tab: listingTab,
  };

  return (
    <Box mt={{ xs: 0, md: 3 }}>
      {!isMediaQueryLarge && (
        <Box onClick={(e) => e.stopPropagation()}>
          <Box width="100%">
            <ActionBarMaybe
              isOwnListing={isOwnListing}
              listing={currentListing}
              editParams={editParams}
            />
          </Box>
        </Box>
      )}
      <SwiperCarousel
        key={currentListingId}
        carouselMedia={renderCarouselMedia({
          shouldDisplayChip: true,
          shouldAutoplayVideo: true,
          shouldMagnify: true,
        })}
        thumbnailImages={renderCarouselMedia()}
        numStockMedia={numStockMedia}
        numSellerMedia={numSellerMedia}
        swiperWrapperRef={swiperWrapperRef}
        isOwnListing={isOwnListing}
        listing={currentListing}
        editParams={editParams}
      />
    </Box>
  );
};

Builder.registerComponent(SectionImages, {
  name: 'SectionImages',
});

export default SectionImages;
